import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faSquareExclamation, faTrash } from '@fortawesome/pro-solid-svg-icons';
import Avatar from '../../../components/Avatar/Avatar';
import Alert from '../../../components/Alert/Alert';
import Badge from '../../UI/Badge/Badge';
import Button, { BUTTON_VARIANTS } from '../../UI/Button/Button';
import Drawer from '../../Drawer/Drawer';
import MenuList, { MenuListItem } from '../../MenuList/MenuList';
import { ReportInDrawer } from '../../Report/Report';
import { Can } from '../../../../ability-context';
import { REPORT_KIND } from '../../../../hooks/useReport';
import createLink from '../../../../libs/createLink';
import { convertDateToStrDuration, isNoFollowProfil } from '../../../../libs/helper';
import { toastError } from '../../../../libs/toast';
import { USER_FIL_ACTUALITY_KEY } from '../../../../api/public-voissa/users';
import { EPHEMERAL_STORY_REPLIES_KEY, deleteStoryReply } from '../../../../api/public-voissa/ephemeralStories';
import './StoryReplies.scss';

/**
 * Une réponse reçue sur sa story (liste ouverte depuis la story et carte privée du mur, cahier des
 * charges V2, visuels 1 bis et 7) : photo, pseudo, badge, date, texte et menu « ••• » : Supprimer,
 * Signaler. Plus de « Répondre en privé » (Bertrand, 19/10).
 * Affiché à l'auteur de la story seulement (les réponses sont privées).
 */
const StoryReplyItem = ({ reply, storyId, ownerId }) => {
    const queryClient = useQueryClient();
    const author = reply.owner || {};
    const relAuthor = isNoFollowProfil(author) ? { rel: 'nofollow' } : {};
    const [menuOpen, setMenuOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const deleteMutation = useMutation(() => deleteStoryReply(storyId, reply._id), {
        onSuccess: () => Promise.all([
            queryClient.invalidateQueries([EPHEMERAL_STORY_REPLIES_KEY, storyId]),
            queryClient.invalidateQueries([USER_FIL_ACTUALITY_KEY, ownerId]),
        ]),
        onError: () => toastError('La réponse n’a pas pu être supprimée.'),
    });

    return (
        <li className={`story-reply-item${deleteMutation.isLoading ? ' is-pending' : ''}`}>
            <Link to={createLink('userProfile', author)} {...relAuthor} className="story-reply-item-avatar">
                <Avatar avatar={author.profile?.avatar} height={36} width={36} gender={author.profile?.gender || 'guest'} alt="" />
            </Link>
            <div className="story-reply-item-body">
                <div className="story-reply-item-name">
                    <Link to={createLink('userProfile', author)} {...relAuthor} className="story-reply-item-author">{author.username}</Link>
                    <Badge user={author} />
                    <span className="story-reply-item-date">{convertDateToStrDuration(reply.createdAt)}</span>
                </div>
                <div className="story-reply-item-text">{reply.text}</div>
            </div>
            <Button onlyIcon small variant={BUTTON_VARIANTS.OUTLINE} className="story-reply-item-options" aria-label="Options" onClick={() => setMenuOpen(true)}>
                <FontAwesomeIcon icon={faEllipsis} />
            </Button>

            <Fragment>
                <Drawer xsmall open={menuOpen} handleClose={() => setMenuOpen(false)} headContent="Options">
                    <MenuList>
                        {/* Droit décidé par CASL, comme pour un commentaire : l'auteur de la story est contentOwner. */}
                        <Can action="delete" subject={reply} modelName="Comment">
                            <MenuListItem icon={faTrash} onClick={() => { setMenuOpen(false); setDeleteOpen(true); }}>Supprimer</MenuListItem>
                        </Can>
                        <MenuListItem icon={faSquareExclamation} onClick={() => { setMenuOpen(false); setReportOpen(true); }}>Signaler</MenuListItem>
                    </MenuList>
                </Drawer>
                <ReportInDrawer
                    targetKind={REPORT_KIND.EPHEMERAL_STORY_REPLY}
                    targetId={reply._id}
                    targetUserId={author._id}
                    open={reportOpen}
                    handleClose={() => setReportOpen(false)}
                />
                <Drawer open={deleteOpen} handleClose={() => setDeleteOpen(false)} headContent="Attention">
                    <Alert
                        text="Voulez-vous vraiment supprimer cette réponse ?"
                        handleDelete={() => { setDeleteOpen(false); deleteMutation.mutate(); }}
                        handleClose={() => setDeleteOpen(false)}
                    />
                </Drawer>
            </Fragment>
        </li>
    );
};

export default StoryReplyItem;
